import { RefObject, useEffect, useState } from "react";

const DEFAULT_GAP = 24; // 1.5rem = 24px, gap-6 (desktop)

/**
 * Hook to calculate how many items fit in a single row of a CSS Grid.
 * Uses ResizeObserver to update on container resize.
 *
 * @param containerRef - Ref to the grid container element
 * @param minItemWidth - Minimum width of each grid item in pixels
 * @param gap - Gap between items in pixels (default: 24px)
 * @returns Number of items that fit in one row
 */
export function useGridRowCount(
  containerRef: RefObject<HTMLDivElement | null>,
  minItemWidth: number,
  gap: number = DEFAULT_GAP
): number {
  const [itemsPerRow, setItemsPerRow] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const calculateItemsPerRow = () => {
      const containerWidth = container.offsetWidth;
      // CSS Grid auto-fill formula: how many items of minItemWidth + gap fit?
      // Each item takes minItemWidth, and there's a gap between items
      // For n items: n * minItemWidth + (n-1) * gap <= containerWidth
      // Solving: n <= (containerWidth + gap) / (minItemWidth + gap)
      const count = Math.floor((containerWidth + gap) / (minItemWidth + gap));
      setItemsPerRow(Math.max(1, count));
    };

    // Calculate immediately
    calculateItemsPerRow();

    // Set up ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(() => {
      calculateItemsPerRow();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, minItemWidth, gap]);

  return itemsPerRow;
}

/**
 * Rounds a count up to the nearest multiple of itemsPerRow
 * to ensure full rows are always displayed.
 *
 * @param count - The desired count of items
 * @param itemsPerRow - Number of items per row
 * @returns Count rounded up to fill complete rows
 */
export function roundToFullRows(count: number, itemsPerRow: number): number {
  if (itemsPerRow <= 0) return count;
  return Math.ceil(count / itemsPerRow) * itemsPerRow;
}
